import { useEffect, useState } from 'react'
import type { Week } from '../lib/types'

/**
 * The line that was missing.
 *
 * A manager arriving on this page has exactly two questions — how long have I
 * got, and how much have I still got to do — and the app answered neither.
 * Six boxes reading "Empty" over a field of black is not a lineup screen, it
 * is a screen that looks broken.
 *
 * The countdown is the whole product in one number. Gridlock's failure mode is
 * not a bad pick, it is no pick, so the deadline should never be something you
 * have to go and work out.
 */
function useCountdown(iso: string | null) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!iso) return
    // Minute resolution. A ticking seconds display on a three-day deadline is
    // decoration that costs a re-render every second.
    const t = setInterval(() => setNow(Date.now()), 30_000)
    return () => clearInterval(t)
  }, [iso])

  if (!iso) return null
  const ms = new Date(iso).getTime() - now
  if (ms <= 0) return { text: 'Locked', urgent: true, done: true }

  const mins = Math.floor(ms / 60_000)
  const days = Math.floor(mins / 1440)
  const hours = Math.floor((mins % 1440) / 60)
  const rem = mins % 60

  const text =
    days > 0 ? `${days}d ${hours}h` : hours > 0 ? `${hours}h ${rem}m` : `${rem}m`

  return { text, urgent: ms < 3 * 60 * 60 * 1000, done: false }
}

export default function WeekBar({
  week, filled, required, unitsLeft,
}: {
  week: Week | undefined
  filled: number
  required: number
  unitsLeft: number
}) {
  const countdown = useCountdown(week?.lock_at ?? null)
  if (!week) return null

  const label = week.label ?? `Week ${week.week_no}`
  const complete = filled >= required

  const kickoff = week.lock_at
    ? new Date(week.lock_at).toLocaleString(undefined, {
        weekday: 'short', month: 'short', day: 'numeric',
        hour: 'numeric', minute: '2-digit',
      })
    : null

  return (
    <section className="weekbar">
      <div className="wb-main">
        <div className="wb-week">
          <span className="wb-phase">
            {week.phase === 'playoff' ? 'Playoffs' : 'Regular season'}
          </span>
          <h1>{label}</h1>
          {kickoff && <p className="wb-kick">First kickoff {kickoff}</p>}
        </div>

        {countdown && (
          <div className={`wb-clock ${countdown.urgent ? 'is-urgent' : ''}`}>
            <span className="wb-clock-n">{countdown.text}</span>
            <span className="wb-clock-l">
              {countdown.done ? 'this week is locked' : 'until picks lock'}
            </span>
          </div>
        )}
      </div>

      <div className="wb-progress">
        <div className="wb-bar" role="progressbar"
             aria-valuenow={filled} aria-valuemin={0} aria-valuemax={required}>
          {Array.from({ length: required }, (_, i) => (
            <span key={i} className={i < filled ? 'wb-pip is-on' : 'wb-pip'} />
          ))}
        </div>
        <p className={complete ? 'wb-status is-good' : 'wb-status'}>
          {complete
            ? 'Lineup set. You can still change it until kickoff.'
            : `${required - filled} of ${required} slots still empty`}
          <span className="muted"> · {unitsLeft} rooms left on your board</span>
        </p>
      </div>
    </section>
  )
}
